import { supabase } from '@/integrations/supabase/client';

export interface SupabaseSupportDoc {
  id: string;
  slug: string;
  title: string;
  content: string;
  category: string;
  subcategory: string | null;
  order_index: number;
  icon: string | null;
  featured_image_url: string | null;
  tags: string[];
  status: 'draft' | 'published' | 'archived';
  helpful_count: number;
  not_helpful_count: number;
  published_at: string;
  updated_at: string;
  created_at: string;
  audience: string[];
  journey_stage: number | null;
  excerpt: string | null;
  topic_type: string | null;
  topic_order: number | null;
}

export interface DocsCategory {
  name: string;
  count: number;
  docs: SupabaseSupportDoc[];
}

export interface DocsParams {
  page?: number;
  per_page?: number;
  search?: string;
  doc_category?: string;
  orderby?: string;
  order?: 'asc' | 'desc';
}

class SupabaseDocsService {
  async fetchPosts(params: DocsParams = {}): Promise<SupabaseSupportDoc[]> {
    const {
      page = 1,
      per_page = 50,
      search,
      doc_category,
      orderby = 'order_index',
      order = 'asc'
    } = params;

    let query = supabase
      .from('support_docs')
      .select('*')
      .eq('status', 'published');

    // Search
    if (search) {
      query = query.or(`title.ilike.%${search}%,content.ilike.%${search}%`);
    }

    // Category filter
    if (doc_category) {
      query = query.eq('category', doc_category);
    }

    // Ordering
    if (orderby === 'order_index') {
      query = query.order('category').order('order_index', { ascending: true });
    } else {
      query = query.order(orderby, { ascending: order === 'asc' });
    }

    // Pagination
    const from = (page - 1) * per_page;
    const to = from + per_page - 1;
    query = query.range(from, to);

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching support docs:', error);
      throw error;
    }

    return (data || []) as SupabaseSupportDoc[];
  }

  async fetchCategories(): Promise<DocsCategory[]> {
    const { data, error } = await supabase
      .from('support_docs')
      .select('*')
      .eq('status', 'published')
      .order('category')
      .order('order_index');

    if (error) {
      console.error('Error fetching doc categories:', error);
      return [];
    }

    // Group by category
    const categoryMap: Record<string, DocsCategory> = {};
    
    data?.forEach((doc) => {
      if (!categoryMap[doc.category]) {
        categoryMap[doc.category] = {
          name: doc.category,
          count: 0,
          docs: []
        };
      }
      categoryMap[doc.category].count++;
      categoryMap[doc.category].docs.push(doc as SupabaseSupportDoc);
    });

    return Object.values(categoryMap);
  }

  async fetchPost(slug: string): Promise<SupabaseSupportDoc | null> {
    const { data, error } = await supabase
      .from('support_docs')
      .select('*')
      .eq('slug', slug)
      .eq('status', 'published')
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Not found
      }
      console.error('Error fetching support doc:', error);
      throw error;
    }

    return data as SupabaseSupportDoc;
  }

  async markHelpful(docId: string, helpful: boolean): Promise<void> {
    const field = helpful ? 'helpful_count' : 'not_helpful_count';
    
    const { error } = await supabase.rpc(`increment_support_doc_${field}`, {
      doc_id: docId
    });

    if (error) {
      console.error('Error marking doc helpful:', error);
    }
  }

  getFeaturedImageUrl(doc: SupabaseSupportDoc): string | null {
    return doc.featured_image_url;
  }

  stripHtml(html: string): string {
    return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  }

  async createDoc(data: {
    title: string;
    slug: string;
    content: string;
    category: string;
    subcategory?: string;
    tags?: string[];
    status?: 'draft' | 'published' | 'archived';
    order_index?: number;
    icon?: string;
    featured_image_url?: string;
  }): Promise<SupabaseSupportDoc> {
    const { data: doc, error } = await supabase
      .from('support_docs')
      .insert({
        ...data,
        tags: data.tags || [],
        status: data.status || 'draft',
        order_index: data.order_index || 0,
        published_at: data.status === 'published' ? new Date().toISOString() : null,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating support doc:', error);
      throw error;
    }

    return doc as SupabaseSupportDoc;
  }

  async updateDoc(id: string, data: Partial<{
    title: string;
    slug: string;
    content: string;
    category: string;
    subcategory: string;
    tags: string[];
    status: 'draft' | 'published' | 'archived';
    order_index: number;
    icon: string;
    featured_image_url: string;
  }>): Promise<SupabaseSupportDoc> {
    const updateData: any = { ...data };
    
    // Set published_at when publishing for the first time
    if (data.status === 'published') {
      updateData.published_at = new Date().toISOString();
    }

    const { data: doc, error } = await supabase
      .from('support_docs')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating support doc:', error);
      throw error;
    }

    return doc as SupabaseSupportDoc;
  }

  async deleteDoc(id: string): Promise<void> {
    const { error } = await supabase
      .from('support_docs')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting support doc:', error);
      throw error;
    }
  }

  async fetchByAudience(audience: string, onboardingOnly?: boolean): Promise<SupabaseSupportDoc[]> {
    let query = supabase
      .from('support_docs')
      .select('*')
      .eq('status', 'published')
      .contains('audience', [audience]);

    if (onboardingOnly) {
      query = query.not('journey_stage', 'is', null)
        .order('journey_stage', { ascending: true });
    } else {
      query = query.is('journey_stage', null)
        .order('category')
        .order('order_index');
    }

    const { data, error } = await query;
    if (error) {
      console.error('Error fetching docs by audience:', error);
      throw error;
    }
    return (data || []) as SupabaseSupportDoc[];
  }

  async fetchDocById(id: string): Promise<SupabaseSupportDoc | null> {
    const { data, error } = await supabase
      .from('support_docs')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      console.error('Error fetching support doc by ID:', error);
      throw error;
    }

    return data as SupabaseSupportDoc;
  }

  async fetchByAudienceGrouped(audience: string): Promise<Record<string, { topic_order: number; docs: SupabaseSupportDoc[] }>> {
    const { data, error } = await supabase
      .from('support_docs')
      .select('*')
      .eq('status', 'published')
      .contains('audience', [audience])
      .not('topic_type', 'is', null)
      .order('topic_order', { ascending: true })
      .order('journey_stage', { ascending: true, nullsFirst: false })
      .order('order_index', { ascending: true });

    if (error) {
      console.error('Error fetching grouped docs:', error);
      throw error;
    }

    const groups: Record<string, { topic_order: number; docs: SupabaseSupportDoc[] }> = {};
    (data || []).forEach((doc) => {
      const key = doc.topic_type as string;
      if (!groups[key]) {
        groups[key] = { topic_order: doc.topic_order as number, docs: [] };
      }
      groups[key].docs.push(doc as SupabaseSupportDoc);
    });

    return groups;
  }

  async searchByAudience(audience: string, query: string): Promise<SupabaseSupportDoc[]> {
    const { data, error } = await supabase
      .from('support_docs')
      .select('*')
      .eq('status', 'published')
      .contains('audience', [audience])
      .or(`title.ilike.%${query}%,excerpt.ilike.%${query}%`)
      .order('topic_order', { ascending: true })
      .order('order_index', { ascending: true })
      .limit(10);

    if (error) {
      console.error('Error searching docs:', error);
      throw error;
    }

    return (data || []) as SupabaseSupportDoc[];
  }
}

export const supabaseDocsService = new SupabaseDocsService();
